import type { CampoDaTela, OpcaoDoCampo, RespostaDaTela } from '../../dominio/operacoesDaTela'
export type { RespostaDaTela } from '../../dominio/operacoesDaTela'

/** Porta explícita do formulário: não acessa DOM arbitrário nem oferece salvar. */
export interface CampoOperavel extends CampoDaTela {
  aplicar: (valor: string) => void
  validar?: (valor: string) => boolean
}
export interface FormularioOperavel {
  /** Inclui usuário, empresa, registro e dependências dos campos. Null enquanto carrega. */
  identidade: () => string | null
  titulo: () => string
  campos: () => CampoOperavel[]
}
const recusar = (codigo: string): RespostaDaTela => ({ status: 'recusado', codigo, salvo: false })
const identificador = (): string => globalThis.crypto.randomUUID()

export function criarCanalDeOperacoesDaTela () {
  let atual: { formulario: FormularioOperavel, instancia: string } | null = null
  const leituras = new Map<string, { instancia: string, identidade: string, escopo: string, ate: number, valores: Map<string, string | null> }>()
  // A última leitura pode reunir seletores consultados separadamente. As opções
  // só sobrevivem enquanto contexto, valores, permissões e catálogos forem iguais.
  let opcoesLidas: {
    assinatura: string
    campos: Map<string, { opcoes: OpcaoDoCampo[], total: number, ate: number }>
  } | null = null
  return {
    registrar (formulario: FormularioOperavel): () => void {
      const registro = { formulario, instancia: identificador() }
      atual = registro
      leituras.clear()
      opcoesLidas = null
      return () => { if (atual === registro) { atual = null; leituras.clear(); opcoesLidas = null } }
    },
    executar (pedido: Record<string, unknown>, escopo: string): RespostaDaTela {
      const registro = atual
      if (!registro) return recusar('tela_indisponivel')
      const identidade = registro.formulario.identidade()
      if (!identidade) { opcoesLidas = null; return recusar('tela_indisponivel') }
      const campos = registro.formulario.campos().slice(0, 20)
      if (pedido.operacao === 'ler') {
        const assinatura = JSON.stringify([registro.instancia, identidade, escopo, campos.map(({ aplicar: _aplicar, validar: _validar, ...campo }) => campo)])
        if (opcoesLidas?.assinatura !== assinatura) opcoesLidas = { assinatura, campos: new Map() }
        const opcoesDoContexto = opcoesLidas.campos
        for (const [id, consulta] of opcoesDoContexto) if (consulta.ate < Date.now()) opcoesDoContexto.delete(id)
        for (const [id, leitura] of leituras) if (leitura.ate < Date.now()) leituras.delete(id)
        if (leituras.size >= 20) leituras.delete(leituras.keys().next().value!)
        const leituraId = identificador()
        const busca = typeof pedido.busca === 'string' ? pedido.busca.trim().toLocaleLowerCase() : ''
        const pagina = Number.isInteger(pedido.pagina) && Number(pedido.pagina) > 0 ? Number(pedido.pagina) : 1
        const resposta: RespostaDaTela = {
          status: 'ok', salvo: false, instancia_id: registro.instancia, leitura_id: leituraId, titulo: registro.formulario.titulo(),
          campos: campos.map(({ aplicar: _aplicar, validar: _validar, opcoes, ...campo }) => {
            if (!opcoes) return campo
            if (pedido.campo_opcoes === campo.id) {
              const encontradas = opcoes.filter(opcao => opcao.rotulo.toLocaleLowerCase().includes(busca))
              opcoesDoContexto.set(campo.id, {
                opcoes: encontradas.slice((pagina - 1) * 20, pagina * 20).map(opcao => ({ ...opcao })),
                total: encontradas.length,
                ate: Date.now() + 60000
              })
            }
            const consulta = opcoesDoContexto.get(campo.id)
            return { ...campo, total_opcoes: consulta?.total ?? opcoes.length, opcoes: consulta?.opcoes.map(opcao => ({ ...opcao })) ?? [] }
          })
        }
        // Reunir opções não renova sua validade. O preenchimento precisa chegar
        // antes do vencimento da consulta mais antiga incluída nesta leitura.
        const ate = Math.min(Date.now() + 60000, ...[...opcoesDoContexto.values()].map(consulta => consulta.ate))
        leituras.set(leituraId, { instancia: registro.instancia, identidade, escopo, ate, valores: new Map(campos.map(campo => [campo.id, campo.valor])) })
        return resposta
      }
      if (pedido.operacao !== 'preencher') return recusar('operacao_invalida')
      const leitura = leituras.get(String(pedido.leitura_id))
      if (!leitura || leitura.ate < Date.now() || leitura.escopo !== escopo || leitura.instancia !== pedido.instancia_id || leitura.instancia !== registro.instancia || leitura.identidade !== identidade) return recusar('leitura_desatualizada')
      const alteracoes = pedido.alteracoes
      if (!Array.isArray(alteracoes) || !alteracoes.length || alteracoes.length > 10) return recusar('alteracoes_invalidas')
      const plano: Array<{ campo: CampoOperavel, valor: string }> = []
      const vistos = new Set<string>()
      for (const alteracao of alteracoes) {
        if (!alteracao || typeof alteracao.campo !== 'string' || typeof alteracao.valor !== 'string' || alteracao.valor.length > 200 || [...alteracao.valor].some(letra => letra.charCodeAt(0) < 32)) return recusar('valor_invalido')
        const campo = campos.find(campo => campo.id === alteracao.campo)
        if (!campo || !campo.editavel || vistos.has(campo.id)) return recusar('campo_nao_editavel')
        if (!leitura.valores.has(campo.id) || leitura.valores.get(campo.id) !== campo.valor) return recusar('campo_alterado_pela_pessoa')
        if (campo.tipo === 'selecao' && !campo.opcoes?.some(opcao => opcao.id === alteracao.valor)) return recusar('opcao_nao_disponivel')
        if (campo.validar && !campo.validar(alteracao.valor)) return recusar('valor_invalido')
        vistos.add(campo.id)
        plano.push({ campo, valor: alteracao.valor })
      }
      leituras.delete(String(pedido.leitura_id))
      opcoesLidas = null
      const aplicados: string[] = []
      try {
        for (const { campo, valor } of plano) { campo.aplicar(valor); aplicados.push(campo.id) }
      } catch {
        // Setters não são transação. Relate exatamente o que já foi aplicado.
        return { ...recusar('falha_ao_preencher'), aplicados }
      }
      return { status: 'ok', aplicados, salvo: false, instancia_id: registro.instancia }
    }
  }
}

export const canalDeOperacoesDaTela = criarCanalDeOperacoesDaTela()
